import { useState } from "react";
import { Box, Container, Typography, Button, TextField, CircularProgress, Alert } from "@mui/material";
import ShieldIcon from "@mui/icons-material/Shield";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import { APIURL } from "../../globals";

const GOLD_GRAD = "linear-gradient(135deg,#D97706,#F59E0B)";
const GOLD      = "#D97706";

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: 14,
    color: "#F1F5F9",
    background: "rgba(255,255,255,.05)",
    borderRadius: "10px",
    "& fieldset": { borderColor: "rgba(148,163,184,.2)" },
    "&:hover fieldset": { borderColor: "rgba(148,163,184,.4)" },
    "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": {
    fontFamily: "IBM Plex Sans, sans-serif",
    fontSize: 14,
    color: "#64748B",
    "&.Mui-focused": { color: GOLD },
  },
};

export default function Slogan({ onRequestDemo }) {
  const [form,    setForm]    = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error,   setError]   = useState("");

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setSuccess(false);
    try {
      const res = await fetch(`${APIURL}/general/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Something went wrong.");
      }
      setSuccess(true);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      setError(err.message || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ background: "#0F172A", py: { xs: 10, md: 14 }, position: "relative", overflow: "hidden" }}>
      {/* Ambient glow */}
      <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(ellipse 50% 60% at 80% 50%, rgba(217,119,6,.08) 0%, transparent 70%)" }} />
      <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none", opacity: .025, backgroundImage: "radial-gradient(rgba(255,255,255,.9) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: { xs: 8, lg: 12 },
          alignItems: "center",
        }}>

          {/* Left — pitch */}
          <Box>
            <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 56, height: 56, borderRadius: "14px", background: GOLD_GRAD, boxShadow: "0 8px 24px rgba(217,119,6,.4)", mb: 3.5 }}>
              <ShieldIcon sx={{ fontSize: 28, color: "#fff" }} />
            </Box>

            <Typography component="h2" className="font-display" sx={{ fontSize: { xs: 28, md: 48 }, fontWeight: 700, color: "#F8FAFC", letterSpacing: "-1.5px", lineHeight: 1.1, mb: 2.5 }}>
              Ready to transform your{" "}
              <Box component="span" sx={{ background: GOLD_GRAD, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                AML programme?
              </Box>
            </Typography>

            <Typography sx={{ fontSize: 16, color: "#64748B", lineHeight: 1.85, mb: 5, fontFamily: "IBM Plex Sans, sans-serif" }}>
              Join businesses across the UAE and MENA using AMLDiligence to stay compliant, reduce regulatory risk, and avoid penalties.
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mb: 6 }}>
              {[
                "No per-hit screening fees",
                "Live in under 48 hours",
                "Dedicated compliance expert included",
                "FATF & UAE CBUAE aligned from day one",
              ].map(item => (
                <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <CheckCircleIcon sx={{ fontSize: 17, color: GOLD, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 14, color: "#94A3B8", fontFamily: "IBM Plex Sans, sans-serif" }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>

          </Box>

          {/* Right — contact form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              background: "rgba(255,255,255,.04)",
              border: "1px solid rgba(148,163,184,.12)",
              borderRadius: "20px",
              p: { xs: 3.5, md: 5 },
              backdropFilter: "blur(12px)",
            }}
          >
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#F1F5F9", mb: .75, fontFamily: "IBM Plex Sans, sans-serif" }}>
              Get in touch
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#64748B", mb: 3.5, fontFamily: "IBM Plex Sans, sans-serif" }}>
              Fill in the form and we'll get back to you within one business day.
            </Typography>

            {success && (
              <Alert
                icon={<CheckCircleIcon />}
                severity="success"
                sx={{ mb: 3, borderRadius: "10px", fontFamily: "IBM Plex Sans, sans-serif", background: "rgba(22,163,74,.12)", color: "#4ADE80", "& .MuiAlert-icon": { color: "#4ADE80" } }}
              >
                Message sent! We'll be in touch shortly.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: "10px", fontFamily: "IBM Plex Sans, sans-serif", background: "rgba(220,38,38,.1)", color: "#F87171", "& .MuiAlert-icon": { color: "#F87171" } }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <TextField label="Full name *" value={form.name}    onChange={set("name")}    required size="small" fullWidth sx={FIELD_SX} />
              <TextField label="Email *"     value={form.email}   onChange={set("email")}   required size="small" fullWidth sx={FIELD_SX} type="email" />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <TextField label="Company"   value={form.company} onChange={set("company")} size="small" fullWidth sx={FIELD_SX} />
              <TextField label="Phone"     value={form.phone}   onChange={set("phone")}   size="small" fullWidth sx={FIELD_SX} type="tel" />
            </Box>
            <TextField
              label="How can we help? *"
              value={form.message}
              onChange={set("message")}
              required multiline rows={4}
              fullWidth sx={{ ...FIELD_SX, mb: 3 }}
            />

            <Button
              type="submit"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
              fullWidth
              sx={{
                py: 1.75, borderRadius: "10px",
                fontSize: 15, fontWeight: 600, textTransform: "none",
                fontFamily: "IBM Plex Sans, sans-serif",
                background: GOLD_GRAD, color: "#fff",
                boxShadow: "0 6px 24px rgba(217,119,6,.35)",
                "&:hover": { background: "linear-gradient(135deg,#B45309,#D97706)", boxShadow: "0 10px 32px rgba(217,119,6,.5)" },
                "&.Mui-disabled": { background: "rgba(217,119,6,.3)", color: "rgba(255,255,255,.5)" },
                transition: "all .22s",
              }}
            >
              {loading ? "Sending…" : "Send Message"}
            </Button>

            <Typography sx={{ fontSize: 12, color: "#334155", mt: 2, textAlign: "center", fontFamily: "IBM Plex Sans, sans-serif" }}>
              We respond within 1 business day. No spam, ever.
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
